<template>
  <div class="page-list">
    <div class="list-top">
      <button v-if="group && group.groupId > 1"
              class="move-tag"
              :disabled="selectedItem.length < 1 || isDemo"
              @click="moveTagName">
        <svg width="16" height="17" viewBox="0 0 16 17" xmlns="http://www.w3.org/2000/svg">
          <g stroke-linecap="round" stroke-linejoin="round" fill="none" fill-rule="evenodd">
            <path d="m11 2.569 3 2-3 2"/>
            <path d="M2 8.569V7.236a2.667 2.667 0 0 1 2.667-2.667H14M5 14.569l-3-2 3-2"/>
            <path d="M14 8.569v1.333a2.667 2.667 0 0 1-2.667 2.667H2"/>
          </g>
        </svg>
        {{ $t('group.editMode.moveToAnotherTag') }}
      </button>
      <div class="search">
        <transition name="slide">
          <div v-if="searching && searchCount > -1"
               class="search-result">
            <button class="reset"
                    @click="resetSearch">
              <i class="icon icon--arrow-left-black"></i>
            </button>

            <span class="count">
              <em>{{ searchCount | thousand }}</em>
              {{ $t('ranking.searchResult') }}
            </span>
          </div>
        </transition>

        <div class="search-option">
          <ba-dropdown :value="searchOption"
                       :items="searchOptionItems"
                       :width="'146px'"
                       @input="onSearchOptionChange" />
        </div>

        <div class="search-input">
          <input type="text"
                 ref="keyword"
                 v-model="keyword"
                 @keyup.enter="onSearch" />
        </div>

        <button class="search-button"
                @click="onSearch">
          <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
            <g fill="none" fill-rule="evenodd" transform="translate(2.5 2.5)" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
              <circle cx="4.5" cy="4.5" r="4.5"/>
              <path d="M10.887 10.887 8 8"/>
            </g>
          </svg>
        </button>
      </div>
    </div>
    <div v-if="!noResult"
         class="list-body">
      <div class="thead">
        <table>
          <colgroup>
            <col style="width: 25px">
            <col style="width: 20%">
            <col style="width: 20%">
            <col style="width: calc(60% - 220px)">
            <col style="width: 170px">
            <col style="width: 25px">
          </colgroup>
          <thead>
            <tr>
              <th class="gap"></th>
              <th v-for="column in columns"
                  :key="`tag-list-${column.text}-${column.value}`"
                  :class="[{ active: listData.length && computedSort === column.value },
                             `th-${column.value}`]"
                  @click="changeSort(column.value)">
                <span v-if="column.value === 'title' && group && group.groupId > 1"
                      @click.stop="onSelectAllItem">
                  <i v-if="selectedCount === 1"
                     class="icon icon--chkbox-on"></i>
                  <i v-else-if="selectedCount === 0"
                     class="icon icon--chkbox-half"></i>
                  <i v-else-if="selectedCount === -1"
                     class="icon icon--chkbox-black"></i>
                </span>
                {{ column.text }}
                <button class="sort"
                        :class="{ active: listData.length && computedSort === column.value,
                                  asc: order,
                                  desc: !order }">
                  <i class="icon icon--sort-asc"></i>
                  <i class="icon icon--sort-desc"></i>
                </button>
              </th>
              <th class="gap"></th>
            </tr>
          </thead>
        </table>
      </div>
      <div class="tbody"
           ref="pageList">
        <table>
          <colgroup>
            <col style="width: 25px">
            <col style="width: 20%">
            <col style="width: 20%">
            <col style="width: calc(60% - 220px)">
            <col style="width: 170px">
            <col style="width: 25px">
          </colgroup>
          <tbody>
            <tr v-for="page in listData"
                :key="`page-item-${page.id}`">
              <td class="gap"></td>
              <template v-for="column in columns">
                <td :class="`td-${column.value}`"
                    :key="`page-item-${page.name}-${column.text}-${column.value}`">
                  <div v-if="column.value === 'regDate'"
                       class="td-data">
                    {{ computedRegDate(page[column.value]) }}
                  </div>
                  <div v-else-if="searchParam && searchCount && searchParam.term === 'title' && column.value === 'title'"
                       class="td-data"
                       :class="{ checkbox: group && group.groupId > 1 }">
                    <ui-checkbox v-if="group && group.groupId > 1"
                                 :value="selectedItem.includes(page.id)"
                                 @input="onSelectItem(page.id)" />
                    <text-highlight :searchKeyword="searchParam ? searchParam.text : ' '"
                                    :textItem="page[column.value] || ''"></text-highlight>
                  </div>
                  <div v-else-if="searchParam && searchCount && searchParam.term === 'pathname' && column.value === 'pathname'"
                       class="td-data">
                    <text-highlight :searchKeyword="searchParam ? searchParam.text : ' '"
                                    :textItem="page[column.value] || ''"></text-highlight>
                  </div>
                  <div v-else-if="column.value === 'title'"
                       class="td-data"
                       :class="{ checkbox: group && group.groupId > 1 }">
                    <ui-checkbox v-if="group && group.groupId > 1"
                                 :value="selectedItem.includes(page.id)"
                                 @input="onSelectItem(page.id)" />
                    {{ page[column.value] }}
                  </div>
                  <div v-else
                       class="td-data">
                    {{ page[column.value] }}
                  </div>
                </td>
              </template>
              <td class="gap"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="list-bottom"
         v-if="!noResult">
      <ui-navigation class="page-navigation"
                     :currentPage="listOption.pageNum"
                     :lastPage="lastPage"
                     @setPage="setPage" />
      <span class="view-per-page">
        {{ $t('common.pagination.viewPerPage') }}
        <ui-selectbox :value="listOption.itemsPerPage"
                      :items="viewPerPageOptionItems"
                      :width="'78px'"
                      :height="'32px'"
                      :slideUp="true"
                      @input="onViewPerPageOptionChange" />
      </span>
    </div>

    <dimmed v-if="moving">
      <tag-select :title="$t('group.editMode.moveToAnotherTag')"
                  :cancel="$t('common.cancel')"
                  :submit="$t('common.move')"
                  :count="count"
                  :group="group"
                  :colorList="colorList"
                  :excludeTagId="tag ? tag.tagId : undefined"
                  @onSubmit="moveTag"
                  @onCancel="cancelMove" />
    </dimmed>
  </div>
</template>

<script>
import { EventBus } from '@/libs/event-bus'
import { getUrlList, getSearch, moveToTag } from '@/api/group'

import TagSelect from '@/components/group/TagSelect'
import Dimmed from '@/components/ui/Dimmed'
import TextHighlight from '@/components/ui/TextHighlight'
import BaDropdown from '@/components/ui/ba/Dropdown'
import UiNavigation from '@/components/ui/UiNavigation'
import UiSelectbox from '@/components/ui/UiSelectbox'
import UiCheckbox from '@/components/ui/UiCheckbox'
import { NOTAG_TAG_ID } from '@/libs/constants'
import { utcToLocalTimezone } from '@/libs/utils'
import { mapState } from 'vuex'
import { format } from 'date-fns'

export default {
  name: 'PageList',
  components: {
    TagSelect,
    Dimmed,
    TextHighlight,
    BaDropdown,
    UiNavigation,
    UiSelectbox,
    UiCheckbox
  },
  props: ['noResult', 'group', 'tag', 'searchParam', 'searchCount', 'count', 'colorList'],
  computed: {
    ...mapState([
      'journeyOptions',
      'isDemo'
    ]),
    searchOptionItems () {
      return [
        { text: this.$t('group.searchOption.pageTitle'), value: 'title' },
        { text: this.$t('group.searchOption.url'), value: 'pathname' }
      ]
    },
    columns () {
      return [
        { text: this.$t('group.column.pageTitle'), value: 'title' },
        { text: this.$t('group.column.domain'), value: 'domain' },
        { text: this.$t('group.column.pathName'), value: 'pathname' },
        { text: this.$t('group.column.collectedDate'), value: 'regDate' }
      ]
    },
    sid () {
      return this.$route.query.sid
    },
    order () {
      return !this.listOption.sort.includes('-')
    },
    computedSort () {
      return this.listOption.sort.replace('-', '')
    },
    selectedCount () {
      if (this.listData.length && this.selectedItem.length >= this.listData.length) {
        return 1
      } else if (this.selectedItem.length > 0) {
        return 0
      } else {
        return -1
      }
    }
  },
  created () {
    if (this.group && this.tag) {
      this.init()
    }
  },
  watch: {
    group (value) {
      if (value) {
        this.resetSearch()
        this.init()
      }
    },

    tag (value, prev) {
      // if (value && (!prev || value.tagId !== prev.tagId)) {
      this.currentGroup = this.group
      this.listOption = {
        pageNum: 1,
        itemsPerPage: this.listOption.itemsPerPage,
        sort: '-regDate'
      }
      this.calcLastPage()
      this.listData = []
      if (this.searchParam) {
        for (let url of this.tag.urls) {
          this.listData.push({
            id: url.gUrlId || '',
            title: url.title || '',
            domain: url.domain || '',
            pathname: url.pathname || '',
            regDate: url.regDate || ''
          })
        }
      } else {
        this.loadUrlList()
      }
      // }
    },

    listData () {
      this.$refs.pageList.scrollTop = 0
      if (!this.moving) {
        this.resetSelectedItem()
      }
    },

    searchParam (value, prev) {
      let v = value ? value.term + value.text : value
      let p = prev ? prev.term + prev.text : prev

      if (v !== p) {
        this.listOption = {
          pageNum: 1,
          itemsPerPage: this.listOption.itemsPerPage,
          sort: '-regDate'
        }
      }
    },

    'listOption.itemsPerPage' (value) {
      this.$emit('setUrlsPerPage', value)
    }
  },
  methods: {
    computedRegDate (value) {
      return format(utcToLocalTimezone(value, this.journeyOptions.timezoneName), 'YYYY-MM-DD')
    },
    init () {
      this.currentGroup = this.group
      if (this.tag) {
        this.calcLastPage()
        this.loadUrlList()
      } else {
        this.listOption = {
          pageNum: 1,
          itemsPerPage: this.listOption.itemsPerPage,
          sort: '-regDate'
        }
        this.lastPage = 1
        this.listData = []
      }
    },

    loadUrlList (force = true) {
      if (this.tag === null) {
        this.listData = []
        return Promise.resolve()
      }

      if (this.searchParam) {
        return this.loadSearchList(force)
      }
      EventBus.$emit('onLoading', true)
      return new Promise(resolve => {
        getUrlList({
          sid: this.sid,
          pageId: this.listOption.pageNum - 1,
          pageNum: this.listOption.itemsPerPage,
          sort: this.listOption.sort,
          groupId: this.group.groupId,
          tagId: this.tag ? this.tag.tagId : null
        }, force).then(result => {
          if (this.tag.tagId === NOTAG_TAG_ID) {
            this.lastUntagUrlListTimeStamp = new Date().getTime()
          }
          this.listData = []
          if (!this.moving) {
            this.resetSelectedItem()
          }
          let i = 0
          for (let url of result) {
            if (i > 100) {
              break
            }
            this.listData.push({
              id: url.gUrlId || '',
              title: url.title || '',
              domain: url.domain || '',
              pathname: url.pathname || '',
              regDate: url.regDate || ''
            })
            i++
          }
          EventBus.$emit('onLoading', false)
          resolve()
        })
      })
    },

    loadSearchList (force = true) {
      EventBus.$emit('onLoading', true)
      return new Promise(resolve => {
        getSearch({
          sid: this.sid,
          pageId: this.listOption.pageNum - 1,
          pageNum: this.listOption.itemsPerPage,
          term: this.searchParam.term,
          text: this.searchParam.text,
          sort: this.listOption.sort,
          groupId: this.group.groupId,
          tagId: this.tag.tagId
        }, force).then(result => {
          this.listData = []
          if (!this.moving) {
            this.resetSelectedItem()
          }
          for (let url of result[0].urls) {
            this.listData.push({
              id: url.gUrlId || '',
              title: url.title || '',
              domain: url.domain || '',
              pathname: url.pathname || '',
              regDate: url.regDate || ''
            })
          }
          EventBus.$emit('onLoading', false)
          resolve()
        })
      })
    },

    calcLastPage () {
      this.lastPage = Math.ceil(this.tag.numOfUrl / this.listOption.itemsPerPage)
    },

    setPage (page) {
      this.listOption.pageNum = page
      this.loadUrlList().then(() => {
        this.$refs.pageList.scrollTop = 0
      })
    },

    changeSort (sort) {
      if (this.listOption.sort === '-' + sort) {
        this.listOption.sort = sort
      } else {
        this.listOption.sort = '-' + sort
      }
      if (this.lastPage > 1) {
        this.loadUrlList()
      } else {
        this.sortInList()
      }
    },

    sortInList () {
      this.listData.sort((a, b) => {
        if (typeof a[this.computedSort] === 'string' && typeof b[this.computedSort] === 'string') {
          if (this.order) {
            return a[this.computedSort].toString().localeCompare(b[this.computedSort].toString())
          } else {
            return b[this.computedSort].toString().localeCompare(a[this.computedSort].toString())
          }
        } else {
          if (this.order) {
            return a[this.computedSort] > b[this.computedSort] ? 1 : -1
          } else {
            return b[this.computedSort] > a[this.computedSort] ? 1 : -1
          }
        }
      })
    },

    onViewPerPageOptionChange (value) {
      this.listOption.itemsPerPage = value
      this.listOption.pageNum = 1
      this.calcLastPage()
      this.loadUrlList()
    },

    onSearchOptionChange (value) {
      this.searchOption = value
    },

    onSearch () {
      if (this.keyword) {
        this.searching = true
        this.$emit('onSearch', {
          term: this.searchOption,
          text: this.keyword
        })
      } else {
        this.searching = false
        this.$emit('onSearch', null)
        this.$emit('noResult', false)
      }
      EventBus.$emit('onSearching', this.searching)
    },

    resetSearch () {
      this.keyword = ''
      this.searchOption = 'title'
      this.searching = false
      this.$emit('onSearch', null)
      this.$emit('noResult', false)
      EventBus.$emit('onSearching', this.searching)
    },

    onSelectItem (item) {
      if (this.selectedItem.includes(item)) {
        this.selectedItem.splice(this.selectedItem.indexOf(item), 1)
      } else {
        this.selectedItem.push(item)
      }
    },

    onSelectAllItem () {
      if (this.selectedCount !== 1) {
        this.resetSelectedItem()
        this.listData.forEach(item => {
          this.selectedItem.push(item.id)
        })
      } else {
        this.resetSelectedItem()
      }
    },

    resetSelectedItem () {
      this.selectedItem = []
    },

    moveTagName () {
      this.moving = true
    },

    moveTag (tag) {
      this.moving = false
      if (this.selectedItem.length > 0) {
        EventBus.$emit('onLoading', true)
        moveToTag({
          sid: this.sid,
          groupId: this.group.groupId,
          fromTagId: this.tag.tagId,
          toTagId: tag.tagId,
          urlIds: this.selectedItem
        }, true).then(() => {
          let timeout = 0
          if (this.tag.tagId === NOTAG_TAG_ID) {
            const currentTimestamp = new Date().getTime()
            let diffTimestamp = 10
            if (this.lastUntagUrlListTimeStamp) {
              diffTimestamp = (currentTimestamp - this.lastUntagUrlListTimeStamp) / 1000
              let second = diffTimestamp > 10 ? 0 : 10 - diffTimestamp
              timeout = second * 1000
            }
          }

          setTimeout(() => {
            this.resetSelectedItem()
            EventBus.$emit('reloadTagList')
            this.loadUrlList(true)
            EventBus.$emit('onLoading', false)
          }, timeout)
        })
      }
    },

    cancelMove () {
      this.moving = false
    }
  },
  data () {
    return {
      currentGroup: null,

      listOption: {
        pageNum: 1,
        itemsPerPage: 50,
        sort: '-regDate'
      },
      lastPage: 0,
      viewPerPageOptionItems: [
        {
          text: '100',
          value: 100
        },
        {
          text: '50',
          value: 50
        },
        {
          text: '20',
          value: 20
        },
        {
          text: '10',
          value: 10
        }
      ],

      keyword: '',
      searching: false,
      searchOption: 'title',
      listData: [],

      selectedItem: [],
      moving: false,
      lastUntagUrlListTimeStamp: null
    }
  }
}
</script>

<style lang="scss" scoped>
.page-list {
  position: relative;
  height: 100%;

  .list-top {
    z-index: 2;
    position: relative;
    margin: 0 -8px;
    padding: 2px 0 2px 35px;
    height: 49px;

    .move-tag {
      font-size: 13px;
      font-weight: 500;
      line-height: 12px;
      color: #ec0047;
      padding: 8px 10px 7px 10px;
      margin: 10px 0 0 9px;
      border-radius: 6px;
      border: solid 1px #ec0047;
      background-color: #fff;
      transition: border-color 0.15s ease,
                  color 0.15s ease,
                  background-color 0.15s ease,
                  opacity 0.15s ease;

      svg {
        vertical-align: middle;
        margin-top: -2px;
        margin-right: 6px;

        g {
          stroke: #ec0047;
          transition: stroke 0.15s ease;
        }
      }

      &:hover {
        background-color: rgba(0, 0, 0, 0.06);
      }

      &:disabled {
        background-color: #fff;
        border-color: #bbb;
        color: #bbb;
        opacity: 0.3;
        cursor: default;

        svg {
          g {
            stroke: #bbb;
          }
        }
      }
    }

    .search {
      position: relative;
      float: right;
      margin-top: 8px;

      .search-result {
        display: inline-block;
        height: 32px;
        vertical-align: top;

        &.slide-enter-active,
        &.slide-leave-active {
          transition: opacity 0.2s ease, transform 0.2s ease;
          z-index: 1;
        }

        &.slide-enter,
        &.slide-leave-to {
          transform: translateX(20px);
          opacity: 0;
        }

        .reset {
          width: 32px;
          height: 32px;
          border-radius: 5px;
          border: solid 1px #bbb;
          background-color: #fff;
          transform: translateY(-1px);
          transition: background-color 0.2s ease,
                      color 0.3s ease;

          &:hover {
            background-color: #e0e0e0;
          }

          &:active {
            background-image: linear-gradient(to top, #777777 0%, #555555 100%);
          }

          i.icon {
            vertical-align: middle;
            transform: translate(-1px, -0.5px);
          }
        }

        .count {
          display: inline-block;
          margin: 10px 15px 0;
          font-size: 12px;
          line-height: 1;
          color: #888;

          em {
            font-weight: 500;
            color: #fe8501;
          }
        }
      }

      .search-option {
        display: inline-block;
        height: 32px;
        margin: 0 5px 0 0;
        vertical-align: top;

        .ui-select {
          /deep/ {
            .item--wrap {
              border: 1px solid #bbb;
              box-shadow: none;
              border-radius: 6px;

              &.safari {
                transform: none;
              }
            }

            .item__list {
              width: calc(100% + 2px) !important;
            }
          }
        }
      }

      .search-input {
        display: inline-block;
        height: 32px;
        width: 155px;
        transition: width 0.2s ease;

        input {
          width: calc(100% - 42px);
          height: 28px;
          margin-top: 1px;
          padding-left: 10px;
          padding-right: 22px;
          line-height: 28px;
          border: none;
          box-shadow: 0 0 0 1px #bbb;
          background-color: #ffffff;
          color: #444444;
          border-radius: 5px;
          transition: box-shadow 0.2s ease;

          &:focus {
            box-shadow: 0 0 0 1px #299eee !important;
          }

          &:disabled {
            opacity: 0.3;
            cursor: default;
          }
        }
      }

      .search-button {
        position: absolute;
        top: 0;
        right: 0;
        width: 32px;
        height: 32px;

        &:disabled {
          opacity: 0.3;
          cursor: default;
        }

        svg {
          position: absolute;
          top: 8px;
          right: 16px;
          width: 16px;
          height: 16px;

          g {
            stroke: #666666;
            transition: stroke 0.3s ease;
          }
        }
      }
    }
  }

  .list-body {
    z-index: 1;
    position: relative;
    height: calc(100% - 106px);
    box-shadow: inset 1px 0 0 rgba(0, 0, 0, 0.12);

    &::after {
      content: "";
      position: absolute;
      z-index: 5;
      bottom: 0;
      right: -10px;
      display: block;
      width: calc(100% + 10px);
      height: 6px;
      opacity: 0.06;
      background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0), #000);
    }

    .thead {
      height: 55px;
    }

    .tbody {
      position: relative;
      max-height: calc(100% - 68px);
      padding-bottom: 12px;
      overflow: auto;
    }

    table {
      position: relative;
      width: 100%;

      thead {
        tr {
          th {
            height: 39px;
            font-size: 13px;
            font-weight: normal;
            background: #333333;
            color: #ffffff;
            padding: 9px 22px 7px 15px;
            vertical-align: bottom;
            text-align: left;
            line-height: 20px;
            transition: background-color 0.15s ease;
            white-space: nowrap;

            .icon {
              vertical-align: middle;
              margin: -1px 5px 1px 0;
              cursor: pointer;
            }

            &:first-child {
              box-shadow: inset 1px 0 0 rgba(0, 0, 0, 0.15);
            }

            &.gap {
              padding: 0;
              width: 25px;
              border-bottom: none;
            }

            .sort {
              display: inline-block;
              width: 14px;
              height: 18px;
              padding: 1px 0;
              margin-top: -2px;
              margin-left: 2px;
              vertical-align: middle;
              border-radius: 4px;
              transition: background-color 0.15s ease,
                          box-shadow 0.15s ease;

              i.icon {
                float: left;
                opacity: 0.2;
                transition: opacity 0.15s ease;
              }

              &.active {
                &.asc {
                  .icon--sort-asc {
                    opacity: 1;
                  }
                }

                &.desc {
                  .icon--sort-desc {
                    opacity: 1;
                  }
                }
              }

              &.active {
                background-color: #272727;
                box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.146);
              }
            }

            &.th-regDate {
              text-align: right;
            }

            &.hover {
              .sort {
                background-color: #272727;
                box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.146);
              }
            }

            &:last-child {
              border-radius: 0 7px 7px 0;
            }
          }
        }
      }

      tbody {
        tr {
          position: relative;
          height: 1px;

          td {
            height: 100%;
            text-align: left;
            font-size: 13px;
            line-height: 1.31;
            letter-spacing: normal;
            color: #444;
            word-break: break-all;
            border-bottom: 1px solid #ebebeb;
            vertical-align: top;

            .td-data {
              position: relative;
              height: 100%;
              padding: 17px 15px 17px 15px;
              box-sizing: border-box;

              &.checkbox {
                padding-left: 34px;
              }

              .ui_checkbox {
                position: absolute;
                top: 15.5px;
                left: 8px;
                margin-left: 2px;
                margin-right: 1px;
              }
            }

            &.gap {
              width: 25px;
              border-bottom: none;
            }

            &.td-regDate {
              text-align: right;

              .td-data {
                margin-right: 20px;
                padding-right: 6px;
              }
            }
          }
        }
      }
    }
  }

  .list-bottom {
    z-index: 2;
    position: relative;
    padding: 1px 0 2px 24px;
    margin: 0 -10px 0 0;
    height: 49px;
    border-top: 1px solid rgba(0, 0, 0, 0.16);
    border-left: 1px solid #ebebeb;

    .page-navigation {
      float: left;
    }

    .view-per-page {
      float: right;
      font-size: 12px;
      color: #767676;
      padding: 0px 26px 0 0;
      margin-top: -5px;

      .ui_select {
        padding-left: 5px;
      }
    }
  }
}
</style>
